import Cookies from "js-cookie";
import db, { getTableCount, updateSyncStatus } from "../db/database";
import { getCurrentEnvironment } from "../utils/businessCentralUtils";
import { pullAperturasFromBC, syncAperturasDeCaja } from "./aperturaDeCajaSyncService";
import { pullBajasDesdeBC } from "./bajasBCService";
import { getActiveLocationCode, getCajaHistorico } from "./cajaEstadoService";
import { syncCajas } from "./cajaSyncService";
import { syncCampaigns } from "./campaignSyncService";
import { syncCierresDeCaja } from "./cierreDeCajaSyncService";
import { syncClientAddresses } from "./clientAddressSyncService";
import { syncClients } from "./clientSyncService";
import { syncColegios } from "./colegioSyncService";
import { syncDivisas } from "./divisaSyncService";
import { syncProductosAdoptados } from "./productoAdoptadoSyncService";
import { syncConfContabilidad } from "./confContabilidadSyncService";
import { syncEmpresa } from "./empresaSyncService";
import { getAllActiveCoupons } from "./couponService";
import { syncFormasDePago } from "./formaDePagoSyncService";
import { syncFormasDePagoPorTienda } from "./formaDePagoPorTiendaSyncService";
import { syncInvoices } from "./invoiceSyncService";
import { syncProductLines } from "./productLinesService";
import { syncProductsForActiveStore, syncProductsStockByLocation } from "./productSyncService";
import { getAllSalesPrices } from "./salesPriceService";
import { syncTPVs } from "./tpvSyncService";
import { syncTurnos } from "./turnoSyncService";
import { syncUsers } from "./userSyncService";
import { syncGruposIVA } from "./grupoIVAService";
import { syncSerieNCF } from "./serieNCFService";
import { syncVendors } from "./vendorService";
import {
  catalogosDeSeleccion,
  fetchSelectOptions,
  todosLosCatalogosDeSeleccion,
} from "./selectOptionService";
import {
  anotarAvisoDeProceso,
  anotarError,
  anotarErrorDeProceso,
  anotarPaso,
  anotarProceso,
  entidadDeTabla,
} from "./syncLog";
import { emitSyncEvent, emitSyncFailuresEvent } from "../utils/syncUtils";
import { COUNTRY_CODES } from "../constants/countries";
import { resolveEffectiveCountryCode } from "../contexts/ConfigContext";

let syncInterval = null;
let onlineListener = null;

let activeSyncUserId = null;

const LEGACY_SYNC_CONFIG_KEY = "syncConfig";

const getResolvedSyncUserId = () => {
  if (activeSyncUserId != null) {
    return activeSyncUserId;
  }
  try {
    const raw = Cookies.get("user");
    if (!raw) {
      return null;
    }
    const u = JSON.parse(raw);
    if (u?.id == null) {
      return null;
    }
    return String(u.id);
  } catch {
    return null;
  }
};

export const getSyncConfigStorageKey = () => {
  const id = getResolvedSyncUserId();
  return id != null ? `syncConfig:user:${id}` : LEGACY_SYNC_CONFIG_KEY;
};

/**
 * Llamar al iniciar/cerrar sesión.
 */
export const setSyncUserContext = (user) => {
  activeSyncUserId = user?.id != null ? String(user.id) : null;
};

const defaultConfig = {
  enabled: true,
  interval: 30,
  intervalUnit: "minutos",
  onStartup: true,
  whenOnline: true,
};

export const getSyncConfig = async () => {
  try {
    const key = getSyncConfigStorageKey();
    let storedConfig = localStorage.getItem(key);

    if (!storedConfig && getResolvedSyncUserId() != null) {
      const legacy = localStorage.getItem(LEGACY_SYNC_CONFIG_KEY);
      if (legacy) {
        localStorage.setItem(key, legacy);
        storedConfig = legacy;
      }
    }

    if (storedConfig) {
      // Mezclar con defaults para que una preferencia guardada antes de agregar
      // una clave nueva no la deje sin valor.
      const parsed = JSON.parse(storedConfig);
      return { ...defaultConfig, ...parsed };
    }
    return defaultConfig;
  } catch (error) {
    anotarErrorDeProceso("no se pudo leer la configuración", error);
    return defaultConfig;
  }
};

export const setSyncConfig = async (config, isUserAuthenticated) => {
  try {
    localStorage.setItem(getSyncConfigStorageKey(), JSON.stringify(config));
    await setupSync(config, { isUserAuthenticated });

    return true;
  } catch (error) {
    anotarErrorDeProceso("no se pudo guardar la configuración", error);
    throw error;
  }
};

const getIntervalInMs = (interval, unit) => {
  const baseMs = interval * 1000;

  switch (unit) {
    case "segundos":
      return baseMs;
    case "minutos":
      return baseMs * 60;
    case "horas":
      return baseMs * 60 * 60;
    default:
      return baseMs * 60;
  }
};

const handleOnlineStatus = () => {
  syncAll();
};

export const setupSync = async (config = null, options = {}) => {
  const { isUserAuthenticated = false } = options;

  clearSync();

  //Si config es null y usuario no esta autenticado, hacer sync inicial (usuario en login screen)
  if (!config && !isUserAuthenticated) {
    await syncAll();
  }

  //Si usuario esta autenticado y config es null, obtenerlo de localStorage
  //traera config default en caso de que no exista
  if (isUserAuthenticated && !config) {
    config = await getSyncConfig();
  }

  //Si config existe y esta habilitado, configurar intervalo de sincronizacion
  if (config && config.enabled) {
    const intervalMs = getIntervalInMs(config.interval, config.intervalUnit);
    syncInterval = setInterval(() => syncAll(), intervalMs);

    if (config.whenOnline) {
      window.removeEventListener("online", handleOnlineStatus);
      window.addEventListener("online", handleOnlineStatus);
      onlineListener = handleOnlineStatus;
    }
  }
};

export const clearSync = () => {
  if (syncInterval) {
    clearInterval(syncInterval);
    syncInterval = null;
  }

  if (onlineListener) {
    window.removeEventListener("online", onlineListener);
    onlineListener = null;
  }
};

export const isOnline = () => {
  return navigator.onLine;
};

export const markAsPending = async (table, id) => {
  try {
    await db[table].update(id, { pendingSync: 1, lastModified: new Date() });
  } catch (error) {
    // Mismo caso que markAsDeleted: si no se relanza, el guardado se anuncia
    // como correcto y el registro nunca se llega a enviar. Acá no se anota el
    // caso correcto porque se llama en cada guardado, incluso dentro de bucles
    // de facturas -- sólo interesa cuando falla.
    anotarError(entidadDeTabla(table), `no se pudo marcar como pendiente (id ${id})`, error);
    throw error;
  }
};

export const markAsDeleted = async (table, id) => {
  try {
    await db[table].update(id, {
      pendingSync: 1,
      deleted: true,
      lastModified: new Date(),
    });

    // El borrado no dejaba ninguna huella en consola: hablaba sólo por el aviso
    // de la pantalla. Con esta línea, filtrar por [sync:tiendas] muestra la vida
    // entera del registro -- desde que se marca hasta que Business Central
    // acepta o rechaza la baja.
    anotarPaso(
      entidadDeTabla(table),
      `marcado para baja (id ${id}) — se pedirá a Business Central al sincronizar`
    );
  } catch (error) {
    anotarError(entidadDeTabla(table), `no se pudo marcar para baja (id ${id})`, error);

    // Se relanza. Sin esto el catch de las siete pantallas era INALCANZABLE:
    // el listado anunciaba "eliminado correctamente" y quitaba la fila aunque
    // la escritura hubiera fallado.
    throw error;
  }
};

export const getPendingSync = async (table) => {
  try {
    // Filtrar por pendingSync = 1 O que No no sea null ni undefined
    return await db[table]
      .filter((item) => item.pendingSync === 1 || item.No === null || item.No === undefined)
      .toArray();
  } catch (error) {
    anotarError(entidadDeTabla(table), "no se pudieron leer los pendientes", error);
    return [];
  }
};

export const markAsSynced = async (table, id) => {
  try {
    await db[table].update(id, { pendingSync: 0, lastSynced: new Date() });
  } catch (error) {
    anotarError(entidadDeTabla(table), `no se pudo marcar como sincronizado (id ${id})`, error);
  }
};

const logSyncTime = (operation, startTime) => {
  const endTime = Date.now();
  const timeInSeconds = ((endTime - startTime) / 1000).toFixed(2);

  // Los trece llamadores pasan "Sincronización de X". El prefijo [sync] ya lo
  // dice, y es la línea más repetida del registro: se recorta acá en vez de
  // tocar los trece.
  const paso = operation.replace(/^Sincronización de /i, "");

  anotarProceso(`${paso} · ${timeInSeconds} s`);
};

/**
 * Cuánto tardó el proceso, en palabras.
 *
 * Debajo del minuto los segundos con un decimal ya son legibles; por encima,
 * "185,4 segundos" obliga a dividir mentalmente.
 *
 * Exportada para poder probarla: es lo único de este aviso que puede salir mal.
 */
export const duracionEnPalabras = (segundos) => {
  const total = Number(segundos);

  if (!Number.isFinite(total)) return "";
  if (total < 60) return `${total.toFixed(1).replace(".", ",")} segundos`;

  // Se redondea ANTES de partir. Redondeando el resto, 119,6 s daba
  // "1 minuto y 60 segundos".
  const redondeado = Math.round(total);
  const minutos = Math.floor(redondeado / 60);
  const resto = redondeado % 60;

  return `${minutos} ${minutos === 1 ? "minuto" : "minutos"}${
    resto > 0 ? ` y ${resto} ${resto === 1 ? "segundo" : "segundos"}` : ""
  }`;
};

// Fallos parciales acumulados de la corrida en curso.
//
// Un fallo parcial es una sincronización que corrió entera pero de la que BC
// rechazó uno o más registros -- típicamente una baja que no puede aplicar por
// integridad referencial ("no se puede eliminar el cajero X: forma parte de
// aperturas de caja"). No es un error de la sincronización, así que no se
// reintenta ni se corta nada, pero tampoco se puede anunciar como completada.
//
// Se acumulan acá en vez de anunciarse en el momento porque el canal por el que
// viajaban, emitSyncEvent, es de una sola línea y el paso siguiente los pisaba.
// Ver SANTPV-59.
let partialFailures = [];

const resetPartialFailures = () => {
  partialFailures = [];
  emitSyncFailuresEvent([]);
};

const takePartialFailures = () => {
  const failures = partialFailures;
  partialFailures = [];
  return failures;
};

// Función para manejar reintentos con visualización
const executeWithRetry = async (syncFunction, operationName, entityKey = null, maxRetries = 5) => {
  let lastError = null;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      if (attempt === 1) {
        emitSyncEvent(`Sincronizando ${operationName}...`);
      } else {
        emitSyncEvent(`Reintentando ${operationName}... (${attempt}/${maxRetries})`);
      }

      const result = await syncFunction();

      // Si la función devuelve false, tratarlo como un error
      if (result === false) {
        throw new Error(`La sincronización de ${operationName} falló`);
      }

      // Fallo parcial: la sincronización corrió, pero BC rechazó o ignoró parte
      // de los registros (ej. una baja de usuario que BC no aplicó). No se
      // reintenta -- el reintento manda exactamente lo mismo y BC contesta
      // igual -- pero tampoco se puede anunciar como completada: se deja
      // registrado el fallo y se avisa con el motivo que devolvió BC.
      if (result && result.success === false) {
        const failMessage =
          result.message || `La sincronización de ${operationName} falló parcialmente`;
        anotarAvisoDeProceso(`${operationName} terminó con fallos — ${failMessage}`);

        if (entityKey) {
          try {
            await updateSyncStatus(entityKey, false, await getTableCount(entityKey), failMessage);
          } catch (error) {
            anotarAvisoDeProceso(
              `no se pudo registrar el estado de ${entityKey} — ${error?.message || error}`
            );
          }
        }

        partialFailures.push({ operationName, message: failMessage });
        emitSyncEvent(failMessage, true);
        return result;
      }

      // Registrar éxito en la base de datos si se proporciona entityKey
      if (entityKey) {
        try {
          const recordCount = await getTableCount(entityKey);
          await updateSyncStatus(entityKey, true, recordCount);
        } catch (error) {
          anotarAvisoDeProceso(
            `no se pudo registrar el estado de ${entityKey} — ${error?.message || error}`
          );
        }
      }

      emitSyncEvent(`Sincronización de ${operationName} completada`, true);

      return result; // Éxito, salir del bucle
    } catch (error) {
      lastError = error;
      anotarErrorDeProceso(`${operationName} falló (intento ${attempt}/${maxRetries})`, error);

      // Errores de precondición (ej. no hay caja/tienda activa) no se
      // resuelven reintentando -- cortar altiro sin esperar ni disparar
      // el modal de error crítico (que auto-recarga la app).
      if (error.nonRetryable) {
        break;
      }

      if (attempt < maxRetries) {
        emitSyncEvent(
          `Error en ${operationName} - Reintentando en 15 segundos... (${attempt}/${maxRetries})`
        );
        await new Promise((resolve) => setTimeout(resolve, 15000)); // Esperar 15 segundos
      }
    }
  }

  // Registrar error en la base de datos si se proporciona entityKey
  if (entityKey) {
    try {
      await updateSyncStatus(entityKey, false, 0, lastError?.message || "Error desconocido");
    } catch (error) {
      anotarAvisoDeProceso(
        `no se pudo registrar el fallo de ${entityKey} — ${error?.message || error}`
      );
    }
  }

  if (lastError?.nonRetryable) {
    throw lastError;
  }

  // Si llegamos aquí, todos los intentos fallaron
  emitSyncEvent(`Error en ${operationName} después de ${maxRetries} intentos`, true);

  // Emitir evento especial para mostrar modal de reinicio
  const criticalErrorEvent = new CustomEvent("criticalSyncError", {
    detail: {
      operationName,
      maxRetries,
      error: lastError,
    },
  });
  window.dispatchEvent(criticalErrorEvent);

  throw lastError;
};

// La sincronización completa que esté corriendo ahora mismo, si hay alguna.
//
// `syncAll` se dispara desde cuatro sitios --el arranque sin sesión, el
// intervalo, el evento `online` y el botón de «Sincronizar todo»-- y no tenía
// guarda: dos de ellos a la vez lanzaban dos corridas enteras en paralelo.
// Medido en Panamá DEV el 20/09/2026: dos lecturas simultáneas de 52.618 líneas
// de producto y dos escrituras de 52.514, 122 y 115 segundos, peleándose por
// IndexedDB. Eso es lo que se veía como que el terminal «se congela».
//
// No se rechaza la segunda: se le devuelve la primera. Quien la pidió acaba
// esperando a una sincronización de verdad y recibe su resultado, que es lo que
// quería. Lo único que pierde una petición que llega a caballo de otra es el
// aviso de duración, porque ese lo enciende la corrida que lo tenía pedido.
let sincronizacionEnCurso = null;

export const syncAll = async (country, options = {}) => {
  if (sincronizacionEnCurso) {
    anotarAvisoDeProceso("ya había una sincronización en curso, se espera a ésa");

    return sincronizacionEnCurso;
  }

  sincronizacionEnCurso = syncAllInterno(country, options);

  try {
    return await sincronizacionEnCurso;
  } finally {
    sincronizacionEnCurso = null;
  }
};

const syncAllInterno = async (country, options = {}) => {
  const { mostrarDuracion = false } = options;

  try {
    if (!isOnline()) {
      return { success: false, message: "Sin conexión a internet" };
    }

    const env = await getCurrentEnvironment();

    anotarProceso("sincronización completa · inicio");

    emitSyncEvent("Iniciando sincronización...");
    resetPartialFailures();
    const totalStartTime = Date.now();
    let startTime = Date.now();

    // La cabecera va en fases. Dentro de cada fase los pasos no se necesitan
    // entre sí, así que corren en paralelo; entre fases sí hay dependencias
    // reales, verificadas en el código y en el AL de Business Central:
    //
    //   tiendas  -> cajas     ("El TPV X no está sincronizado con Business Central")
    //   tiendas  -> usuarios  (la tienda forma parte de la clave del cajero)
    //   cajas    -> aperturas ("No existe la caja X en la tienda Y", en el AL)
    //   turnos   -> aperturas (una apertura sin turno con No no se envía)
    //   aperturas-> cierres   (buildShiftContext resuelve la apertura del cierre)
    //   clientes -> facturas, direcciones
    //
    // El orden anterior violaba dos de esas: usuarios corría ANTES que tiendas, y
    // aperturas ANTES que cajas. Con datos ya sincronizados no se notaba --todo
    // existía de corridas previas-- pero una caja nueva y una apertura nueva en
    // la misma sincronización terminaban con la apertura rechazada.
    const faseEnParalelo = async (etiqueta, pasos) => {
      anotarProceso(`fase · ${etiqueta} (en paralelo)`);
      const inicio = Date.now();

      // allSettled y no all: con `all`, el primer fallo deja el resto de la fase
      // a medio camino y sin registrar su estado. Acá se deja terminar a todos y
      // recién entonces se propaga el primer error, para no cambiar lo que
      // syncAll hacía ante un fallo.
      const resultados = await Promise.allSettled(
        pasos.map(([fn, nombre, clave]) => executeWithRetry(fn, nombre, clave))
      );

      logSyncTime(`Sincronización de ${etiqueta}`, inicio);

      const fallo = resultados.find((r) => r.status === "rejected");
      if (fallo) throw fallo.reason;
    };

    // Fase 0: las bajas hechas EN Business Central, ANTES de traer nada.
    //
    // Estaba al final, "sobre todo lo demás ya traído", y ese orden tiene un
    // problema en un terminal recién instalado: la base se llena con el estado
    // actual de BC y recién entonces se reproducen las bajas, así que un
    // registro borrado y vuelto a crear con la misma clave se borraría de local
    // estando vivo en BC. Corriendo primero, la base todavía está vacía: no hay
    // nada que borrar de más, y lo que BC sí tiene entra después, en su fase.
    //
    // Va en su propio try/catch: es una reconciliación, no un paso del que
    // dependa nada. Si el servicio web no está publicado en el entorno se anota
    // y la sincronización sigue.
    try {
      startTime = Date.now();
      await pullBajasDesdeBC();
      logSyncTime("Sincronización de bajas de Business Central", startTime);
    } catch (error) {
      anotarErrorDeProceso("las bajas de Business Central fallaron, no bloquean el resto", error);
    }

    // Fase 1: empresa y tiendas. Tiendas es prerequisito de casi todo lo demás,
    // así que va sola y antes.
    startTime = Date.now();
    await executeWithRetry(() => syncEmpresa(), "empresa", "empresa");
    logSyncTime("Sincronización de información de empresa", startTime);

    startTime = Date.now();
    await executeWithRetry(() => syncTPVs(), "tiendas", "tpvs");
    logSyncTime("Sincronización de tiendas", startTime);

    // Fase 2: todo lo que sólo necesitaba las tiendas, o nada.
    await faseEnParalelo("usuarios, turnos, cajas y clientes", [
      [() => syncUsers(), "usuarios", "users"],
      [() => syncTurnos(), "turnos", "turnos"],
      [() => syncCajas(), "cajas", "cajas"],
      [() => syncClients(), "clientes", "clients"],
    ]);

    // Fase 3: lo que cuelga de clientes.
    await faseEnParalelo("direcciones y facturas", [
      [() => syncClientAddresses(), "direcciones", "clientAddresses"],
      [() => syncInvoices(country), "facturas", "invoices"],
    ]);

    // Fase 4: la cadena final, que sí es estrictamente secuencial.
    startTime = Date.now();
    await executeWithRetry(() => syncAperturasDeCaja(), "aperturas de caja", "aperturaDeCaja");
    logSyncTime("Sincronización de aperturas de caja", startTime);

    // Traer histórico de aperturas de caja, mismo período (Caja/Fechas) de
    // la última corrida exitosa de Traer Históricos. Sin bookmark (nunca
    // se usó TH), no se intenta nada. Se envuelve en su propio try/catch
    // -- a diferencia del push de arriba, esto es best-effort: si falla no
    // debe abortar el resto de syncAll (cierres, etc.), ya que es una
    // capacidad nueva y menos probada que el push.
    const cajaHistoricoParaPull = getCajaHistorico();
    if (cajaHistoricoParaPull?.FechaInicio && cajaHistoricoParaPull?.FechaFin) {
      try {
        startTime = Date.now();
        await executeWithRetry(
          () =>
            pullAperturasFromBC({
              fechaInicio: cajaHistoricoParaPull.FechaInicio,
              fechaFin: cajaHistoricoParaPull.FechaFin,
              caja: cajaHistoricoParaPull.Caja,
            }).then((r) => r.success),
          "histórico de aperturas de caja",
          "aperturaDeCaja"
        );
        logSyncTime("Sincronización de histórico de aperturas de caja", startTime);
      } catch (error) {
        anotarError("aperturas de caja", "falló el histórico", error);
      }
    }

    startTime = Date.now();
    await executeWithRetry(() => syncCierresDeCaja(), "cierres de caja", "cierreDeCaja");
    logSyncTime("Sincronización de cierres de caja", startTime);

    // Productos va al final, después de los catálogos -- ver más abajo.

    // A partir de acá, los catálogos no se leen entre sí ni dependen de la
    // cadena secuencial de arriba (confirmado revisando sus imports) -- se
    // sincronizan en paralelo para reducir el tiempo total, en vez de un
    // await uno detrás de otro. Cada uno sigue usando su propio
    // executeWithRetry (mismo registro de estado/reintentos por catálogo en
    // Configuración), pero envuelto en su propio try/catch: si uno falla no
    // bloquea ni aborta a los demás (antes, cualquier catálogo fallido
    // detenía TODO lo que venía después, sin relación entre sí).
    const fetchVendedores =
      env.country === COUNTRY_CODES.HONDURAS ? syncVendors : () => fetchSelectOptions("Vendedores");

    const catalogSyncs = [
      { fn: () => syncFormasDePago(), label: "formas de pago", key: "formasPago" },
      // Va junto al catálogo y no dentro de él: son dos preguntas distintas
      // --cuáles existen, y cuáles puede cobrar cada tienda-- y el catálogo se
      // sigue trayendo entero para poder nombrar documentos de otras tiendas.
      {
        fn: () => syncFormasDePagoPorTienda(),
        label: "formas de pago por tienda",
        key: "formasPagoPorTienda",
      },
      { fn: () => syncColegios(), label: "colegios", key: "colegios" },
      { fn: () => getAllActiveCoupons(), label: "cupones", key: "coupons" },
      { fn: () => getAllSalesPrices(), label: "precios", key: "salesPrices" },
      { fn: () => syncCampaigns(), label: "campañas", key: "campaigns" },
      { fn: () => syncGruposIVA(), label: "grupos IVA", key: "grupoIVA" },
      // Divisas y tipo de cambio, todos los países. Va en cada sincronización
      // completa (inicio de sesión e intervalo): al menos una lectura al día.
      { fn: () => syncDivisas(), label: "divisas", key: "divisas" },
      { fn: fetchVendedores, label: "vendedores", key: "vendedores" },
      // Las líneas de producto NO van aquí: se fueron con los productos, a la
      // fase de abajo. Ver el comentario de esa fase.
      // Los catálogos de selección salen del propio endpointConfig, filtrados
      // por país. Antes estaban escritos a mano acá Y en syncSingleModel, y las
      // dos listas ya habían divergido.
      ...catalogosDeSeleccion(resolveEffectiveCountryCode(env.country)).map((catalogo) => ({
        fn: () => fetchSelectOptions(catalogo.endpoint),
        label: catalogo.label,
        key: catalogo.table,
      })),
    ];

    // El país sale de resolveEffectiveCountryCode y no de env.country directo,
    // para que los catálogos de un país también sincronicen cuando se prueba ese
    // país vía REACT_APP_COUNTRY_OVERRIDE sobre un entorno real de otro. Antes
    // Perú lo hacía así y Paraguay no, sin más motivo que el orden en que se
    // fueron agregando; ahora los dos siguen el mismo criterio.
    if (COUNTRY_CODES.HONDURAS === env.country) {
      catalogSyncs.push({ fn: () => syncSerieNCF(), label: "serie NCF", key: "serieNCF" });
    }

    // Las listas escolares (colegio + grado -> productos) sólo existen en Perú.
    if (resolveEffectiveCountryCode(env.country) === COUNTRY_CODES.PERU) {
      catalogSyncs.push({
        fn: () => syncProductosAdoptados(),
        label: "productos adoptados",
        key: "productosAdoptados",
      });
      // De la configuración contable sale el impuesto a las bolsas de plástico
      // (ICBPER), que sólo se cobra en Perú. Ver utils/impuestoDeBolsas.js.
      catalogSyncs.push({
        fn: () => syncConfContabilidad(),
        label: "configuración contable",
        key: "confContabilidad",
      });
    }

    anotarProceso(`fase · ${catalogSyncs.length} catálogos (en paralelo)`);
    const catalogsStartTime = Date.now();
    await Promise.all(
      catalogSyncs.map(async ({ fn, label, key }) => {
        const stepStart = Date.now();
        try {
          await executeWithRetry(fn, label, key);
          logSyncTime(`Sincronización de ${label}`, stepStart);
        } catch (error) {
          anotarErrorDeProceso(`${label} falló, no bloquea el resto de la sincronización`, error);
        }
      })
    );
    logSyncTime("Sincronización de catálogos en paralelo", catalogsStartTime);

    // Productos: al final y sólo si hay una caja abierta con tienda configurada.
    //
    // El catálogo es POR TIENDA, así que sin caja abierta no hay tienda que
    // consultar -- y ese es el caso normal, no un error: se omite y listo. Se
    // llama a syncProductsStockByLocation y no a syncProductsForActiveStore
    // precisamente por eso: la segunda lanza NO_ACTIVE_LOCATION, que acá sería
    // ruido.
    //
    // Va DESPUÉS de los catálogos por dos razones, las dos aprendidas por las
    // malas al ponerlo antes:
    //
    // 1. syncProductsStockByLocation lee db.grupoIVA para calcular el IVA de
    //    cada producto, y grupoIVA se sincroniza dentro del bloque de catálogos.
    //    Corriendo antes, justo después de borrar los maestros, ese catálogo
    //    está vacío y todos los productos quedan con IVA 0 -- sin ningún error.
    // 2. executeWithRetry lanza si la función devuelve false. Puesto antes,
    //    un fallo de productos abortaba syncAll y se llevaba por delante TODO
    //    el bloque de catálogos, incluidas las líneas de producto.
    //
    // Por lo segundo va además con su propio try/catch, igual que cada catálogo:
    // que productos falle no debe tumbar una sincronización que ya terminó.
    const activeLocationCode = await getActiveLocationCode();

    // Las líneas de producto van con los productos, y no con los catálogos.
    //
    // Describen la composición de los productos de la tienda, así que sin
    // productos no las usa nadie: sus tres únicos lectores --la ficha de
    // producto, el listado y la línea de factura-- necesitan un producto
    // delante. Es el mismo razonamiento que ya justificaba borrarlas al cerrar
    // caja, aplicado a la otra punta: si no tiene sentido conservarlas sin
    // catálogo, tampoco tiene sentido traerlas.
    //
    // Y no era gratis. Al cerrar caja se borran las 52.514 líneas; a partir de
    // ahí cada sincronización volvía a pedírselas enteras a Business Central
    // --el checkpoint incremental sale de las que hay guardadas, y no hay
    // ninguna-- y a reescribirlas. Dos minutos, en la pantalla de acceso, con
    // la caja cerrada, para dejar en la base algo que nada puede leer. Medido
    // en Panamá DEV el 20/09/2026: 122 s.
    if (activeLocationCode) {
      anotarProceso("fase · productos de la tienda activa");
      startTime = Date.now();
      try {
        await executeWithRetry(
          () => syncProductsStockByLocation(activeLocationCode),
          "productos",
          "products"
        );
        logSyncTime("Sincronización de productos", startTime);
      } catch (error) {
        anotarErrorDeProceso("productos falló, no bloquea el resto de la sincronización", error);
      }

      startTime = Date.now();
      try {
        await executeWithRetry(() => syncProductLines(), "líneas de producto", "productLines");
        logSyncTime("Sincronización de líneas de producto", startTime);
      } catch (error) {
        anotarErrorDeProceso(
          "líneas de producto falló, no bloquea el resto de la sincronización",
          error
        );
      }
    } else {
      anotarAvisoDeProceso(
        "productos y sus líneas omitidos, no hay una caja abierta con tienda configurada"
      );
    }

    const totalTime = ((Date.now() - totalStartTime) / 1000).toFixed(2);
    anotarProceso(`sincronización completa · terminada en ${totalTime} s`);

    // El aviso de duración se PIDE, no se hereda de "no es silenciosa".
    //
    // Al principio se ataba a `!silent`, y eso lo mostraba también en la
    // sincronización que corre sola al abrir la pantalla de acceso: nadie la
    // pidió y el modal aparecía igual. Ahora sólo lo enciende el botón
    // "Sincronizar todo", que es el único sitio donde alguien está esperando
    // una respuesta.
    const avisarDuracion = (nivel, titulo) => {
      if (!mostrarDuracion) return;
      window.messageApi?.[nivel](`El proceso tardó ${duracionEnPalabras(totalTime)}.`, {
        modal: true,
        title: titulo,
      });
    };

    // Los registros que BC rechazó se cuentan una sola vez, al final y todos
    // juntos, en un panel que se queda hasta que lo cierren. Anunciarlos en el
    // momento no servía: el paso siguiente pisaba el mensaje.
    const failures = takePartialFailures();

    if (failures.length > 0) {
      emitSyncFailuresEvent(failures);
      const aviso =
        failures.length === 1
          ? "Sincronización completada con 1 aviso"
          : `Sincronización completada con ${failures.length} avisos`;
      emitSyncEvent(aviso, true);
      // Los avisos se detallan en su propio panel; acá sólo se dice que los hubo,
      // para no anunciar como impecable una corrida que dejó registros sin aplicar.
      avisarDuracion("warning", aviso);
      return { success: true, message: aviso, failures };
    }

    emitSyncEvent("Sincronización completada", true);
    avisarDuracion("success", "Sincronización completada");
    return { success: true, message: "Sincronización completada" };
  } catch (error) {
    anotarErrorDeProceso("la sincronización se interrumpió", error);

    // Si BC ya había rechazado registros antes de que esto se cayera, ese
    // motivo se pierde con la excepción salvo que se cuente acá también.
    const failures = takePartialFailures();
    if (failures.length > 0) emitSyncFailuresEvent(failures);

    emitSyncEvent("Error en sincronización", true);
    return {
      success: false,
      message: "Error durante la sincronización: " + error.message,
    };
  }
};

/**
 * Evita la sincronización completa de maestros (productos, clientes, etc.).
 */
export const syncAfterCierreDeCaja = async (country) => {
  if (!isOnline()) {
    throw new Error("Sin conexión a internet");
  }

  if (!country?.code) {
    throw new Error("No hay configuración de país; no se puede sincronizar facturas");
  }

  anotarProceso("sincronización posterior al cierre de caja · inicio");
  emitSyncEvent("Sincronizando datos del cierre…");
  resetPartialFailures();

  const syncInvoicesForCierre = async () => {
    const r = await syncInvoices(country);
    if (!r || r.success === false) {
      throw new Error(r?.message || "Error al sincronizar facturas pendientes");
    }
    return true;
  };

  // Los avisos del cierre --una venta que no llegó a Business Central y por eso
  // no va en el detalle-- suben hasta la pantalla que acaba de cerrar la caja.
  // Sin esto se anotaban en la consola y el cajero no se enteraba. Acuerdo
  // `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H4.
  let avisosDelCierre = [];

  const syncCierresForCierre = async () => {
    const results = await syncCierresDeCaja();
    avisosDelCierre = results.flatMap((row) => row.avisos || []);
    const failed = results.filter((row) => !row.success);
    if (failed.length > 0) {
      throw new Error(
        failed[0]?.message || "No se pudo sincronizar el cierre de caja con Business Central"
      );
    }
    return true;
  };

  let startTime = Date.now();
  await executeWithRetry(syncInvoicesForCierre, "facturas pendientes", "invoices");
  logSyncTime("Sincronización de facturas (post-cierre)", startTime);

  startTime = Date.now();
  await executeWithRetry(() => syncTurnos(), "turnos", "turnos");
  logSyncTime("Sincronización de turnos (post-cierre)", startTime);

  startTime = Date.now();
  await executeWithRetry(() => syncAperturasDeCaja(), "aperturas de caja", "aperturaDeCaja");
  logSyncTime("Sincronización de aperturas de caja (post-cierre)", startTime);

  startTime = Date.now();
  await executeWithRetry(syncCierresForCierre, "cierres de caja", "cierreDeCaja");
  logSyncTime("Sincronización de cierres de caja (post-cierre)", startTime);

  const failures = takePartialFailures();

  if (failures.length > 0) {
    emitSyncFailuresEvent(failures);
    const aviso =
      failures.length === 1
        ? "Sincronización del cierre completada con 1 aviso"
        : `Sincronización del cierre completada con ${failures.length} avisos`;
    emitSyncEvent(aviso, true);
    anotarAvisoDeProceso("sincronización posterior al cierre de caja · terminada con avisos");
    return { success: true, message: aviso, failures, avisos: avisosDelCierre };
  }

  emitSyncEvent("Sincronización del cierre completada", true);
  anotarProceso("sincronización posterior al cierre de caja · terminada");
  return {
    success: true,
    message: "Sincronización del cierre completada",
    avisos: avisosDelCierre,
  };
};

export const markForSync = markAsPending;
export const markForDelete = markAsDeleted;

// Funciones para sincronizar modelos individuales
export const syncSingleModel = async (modelKey, country) => {
  try {
    if (!isOnline()) {
      throw new Error("Sin conexión a internet");
    }

    anotarProceso(`sincronización individual · ${modelKey}`);

    // Las bajas hechas EN Business Central también acá, no sólo en la
    // sincronización completa: desde Configuración se sincroniza un modelo
    // suelto, y sin este paso una baja hecha en BC no se aplicaba nunca por esa
    // vía. Es una lectura global y compartida, así que no importa qué modelo
    // se haya pedido. No bloquea: si falla, el modelo se sincroniza igual.
    try {
      await pullBajasDesdeBC();
    } catch (error) {
      anotarErrorDeProceso("las bajas de Business Central fallaron, no bloquean el resto", error);
    }

    const fetchVendedores =
      country?.code === COUNTRY_CODES.HONDURAS
        ? syncVendors
        : () => fetchSelectOptions("Vendedores");

    const modelMappings = {
      users: { func: syncUsers, name: "usuarios" },
      clients: { func: syncClients, name: "clientes" },
      products: { func: syncProductsForActiveStore, name: "productos" },
      productLines: { func: syncProductLines, name: "líneas de producto" },
      invoices: { func: () => syncInvoices(country), name: "facturas" },
      turnos: { func: syncTurnos, name: "turnos" },
      cajas: { func: syncCajas, name: "cajas" },
      aperturaDeCaja: {
        func: async () => {
          const pushOk = await syncAperturasDeCaja();
          const cajaHistorico = getCajaHistorico();
          if (cajaHistorico?.FechaInicio && cajaHistorico?.FechaFin) {
            try {
              await pullAperturasFromBC({
                fechaInicio: cajaHistorico.FechaInicio,
                fechaFin: cajaHistorico.FechaFin,
                caja: cajaHistorico.Caja,
              });
            } catch (error) {
              anotarError("aperturas de caja", "falló el histórico", error);
            }
          }
          return pushOk;
        },
        name: "aperturas de caja",
      },
      cierreDeCaja: { func: syncCierresDeCaja, name: "cierres de caja" },
      // Las dos juntas, y a propósito. Sincronizar sólo el catálogo dejaba la
      // tabla por tienda con el recorte de la vez anterior: la pantalla enseña
      // el cruce de ambas, así que refrescar una sola da una foto mentirosa.
      formasPago: {
        func: async () => {
          const ok = await syncFormasDePago();
          await syncFormasDePagoPorTienda();
          return ok;
        },
        name: "formas de pago",
      },
      formasPagoPorTienda: {
        func: syncFormasDePagoPorTienda,
        name: "formas de pago por tienda",
      },
      colegios: { func: syncColegios, name: "colegios" },
      tpvs: { func: syncTPVs, name: "tiendas" },
      coupons: { func: getAllActiveCoupons, name: "cupones" },
      campaigns: { func: syncCampaigns, name: "campañas" },
      salesPrices: { func: getAllSalesPrices, name: "precios" },
      empresa: { func: syncEmpresa, name: "empresa" },
      vendedores: { func: fetchVendedores, name: "vendedores" },
      serieNCF: { func: syncSerieNCF, name: "serieNCF" },
      grupoIVA: { func: syncGruposIVA, name: "grupo IVA" },
      divisas: { func: syncDivisas, name: "divisas" },
      productosAdoptados: { func: syncProductosAdoptados, name: "productos adoptados" },
      confContabilidad: { func: syncConfContabilidad, name: "configuración contable" },

      // Los catálogos de selección salen del mismo sitio que en syncAll, pero sin
      // filtrar por país: si la tarjeta está en pantalla es porque el modelo
      // existe, y su botón individual debe funcionar aunque ese catálogo no
      // entre en la sincronización completa de este país.
      ...Object.fromEntries(
        todosLosCatalogosDeSeleccion().map((catalogo) => [
          catalogo.table,
          { func: () => fetchSelectOptions(catalogo.endpoint), name: catalogo.label },
        ])
      ),
    };

    const model = modelMappings[modelKey];
    if (!model) {
      throw new Error(`Modelo ${modelKey} no encontrado`);
    }

    const result = await executeWithRetry(
      model.func,
      model.name,
      modelKey,
      3 // Solo 3 intentos para sincronización manual
    );

    takePartialFailures();

    // La sincronización pudo correr y aun así dejar registros sin aplicar en BC:
    // se propaga tal cual para que la pantalla lo muestre como fallo.
    if (result && result.success === false) {
      return { success: false, message: result.message };
    }

    return { success: true, message: `${model.name} sincronizado correctamente` };
  } catch (error) {
    anotarErrorDeProceso(`falló la sincronización individual de ${modelKey}`, error);
    throw error;
  }
};

// Borrar maestro individualmente
export const deleteModelData = async (model) => {
  const { key, name } = model;
  try {
    if (!db[key]) {
      window.messageApi.error(`La tabla '${key}' no existe.`);
      return { success: false };
    }

    window.messageApi.loading(`Eliminando '${name}'...`, 0);

    await db[key].clear();

    //elimino metadatos de tabla eliminada
    const metadataDeletedTable = await db.syncMetadata.where("entity").equals(key).first();

    if (metadataDeletedTable) {
      await db.syncMetadata.put({
        ...metadataDeletedTable,
        lastSyncDate: null,
        syncStatus: null,
        lastSuccessfulSync: null,
        totalRecords: 0,
      });
    }

    window.messageApi.destroy();

    window.messageApi.success(`Los datos de '${name}' fueron eliminados correctamente.`);
  } catch (error) {
    anotarErrorDeProceso(`no se pudieron eliminar los datos de ${key}`, error);

    window.messageApi.destroy();
    window.messageApi.error(`Error al eliminar los datos: ${error?.message}`);
  }
};
